
import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import Navbar from '@/components/Navbar';
import Hero from '@/components/Hero';
import Services from '@/components/Services';
import HowWeWork from '@/components/HowWeWork';
import KeyMetrics from '@/components/KeyMetrics';
import Contact from '@/components/Contact';
import Footer from '@/components/Footer';
import { Toaster } from "@/components/ui/toaster";
import { trackPageVisit, startPageTracking } from '@/utils/analytics';
import TechServices from '@/components/TechServices';
import LegalServices from '@/components/LegalServices';

const canonicalUrl = "https://nextgenstartup.ai/";

const seo = {
  title: "Launch Your AI-Powered Business | NextGen Startup Solutions",
  description: "Transform your business ideas into reality with NextGen Startup's AI-powered tools, expert strategies, and proven growth frameworks. Start your journey today.",
  url: canonicalUrl,
  ogImage: "/assets/og-image.png",
};

const structuredData = {
  "@context": "https://schema.org",
  "@type": "Organization",
  "name": "NextGen Startup",
  "description": seo.description,
  "url": seo.url,
  "logo": "https://nextgenstartup.com/logo.png",
  "sameAs": [
    "https://instagram.com/nextgen.startup"
  ],
  "contactPoint": {
    "@type": "ContactPoint",
    "telephone": "+9613102322",
    "contactType": "customer service",
    "availableLanguage": ["English"]
  }
};

const Index = () => {
  const location = useLocation();

  useEffect(() => {
    startPageTracking();
    const cleanup = () => {
      trackPageVisit(location.pathname);
    };
    
    return cleanup;
  }, [location.pathname]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    const handleNavLinkClick = (event) => {
      const navLink = event.target.closest('a[href^="#"]');
      if (!navLink) return;
      
      const targetId = navLink.getAttribute('href').substring(1);
      const targetSection = document.getElementById(targetId);
      
      if (targetSection) {
        event.preventDefault();
        const navbarHeight = 64; // Approximate height of navbar
        
        // Find the emoji element within the section (typically in the SectionHeader component)
        // It's usually the first motion.div with a text-xl/text-2xl class containing the emoji
        const emojiElement = targetSection.querySelector('.text-xl, .text-2xl');
        
        if (emojiElement) {
          // Calculate position of the emoji element
          const emojiPosition = emojiElement.getBoundingClientRect().top + window.pageYOffset;
          
          // Add a small offset to position the emoji nicely in view
          const scrollPosition = emojiPosition - navbarHeight - 20; // 20px extra padding
          
          window.scrollTo({
            top: scrollPosition,
            behavior: 'smooth'
          });
        } else {
          // Fallback to the section top if emoji not found
          const targetPosition = targetSection.getBoundingClientRect().top + window.pageYOffset;
          window.scrollTo({
            top: targetPosition - navbarHeight,
            behavior: 'smooth'
          });
        }
      }
    };
    
    document.addEventListener('click', handleNavLinkClick);
    
    return () => {
      document.removeEventListener('click', handleNavLinkClick);
    };
  }, []);

  return (
    <div className="min-h-screen relative bg-gradient-to-br from-[#001233] to-[#023047] text-white">
      {/* Tech-style grid pattern overlay for entire page */}
      <div className="fixed inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNDAiIGhlaWdodD0iNDAiIHZpZXdCb3g9IjAgMCA0MCA0MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxwYXRoIGQ9Ik0wIDBoNDB2NDBoLTQweiIvPjxwYXRoIGQ9Ik0wIDBoMXY0MGgtMXpNMzkgMGgxdjQwaC0xek0wIDBoNDB2MWgtNDB6TTAgMzloNDB2MWgtNDB6IiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz48L2c+PC9zdmc+')] opacity-30 z-0"></div>
      
      {/* Global tech-style glow effects */}
      <div className="fixed top-0 right-0 w-[40vw] h-[40vh] rounded-full bg-[#33C3F0]/20 blur-3xl z-0"></div>
      <div className="fixed bottom-0 left-0 w-[60vw] h-[30vh] rounded-full bg-[#FF6B6B]/20 blur-3xl z-0"></div>
      <div className="fixed top-1/4 left-1/4 w-[20vw] h-[20vh] rounded-full bg-purple-300/10 blur-3xl z-0"></div>
      
      {/* Dynamic color accent elements */}
      <div className="fixed top-[15%] left-[10%] w-20 h-20 rounded-full bg-[#9b00ff]/10 blur-2xl z-0 animate-pulse-slow"></div>
      <div className="fixed bottom-[20%] right-[15%] w-32 h-32 rounded-full bg-blue-400/10 blur-2xl z-0 animate-float"></div>
      <div className="fixed top-[40%] right-[10%] w-24 h-24 rounded-full bg-purple-500/8 blur-2xl z-0 animate-bounce-slow"></div>
      
      <Helmet>
        <title>{seo.title}</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={seo.url} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={seo.url} />
        <meta property="og:image" content={seo.ogImage} />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={seo.title} />
        <meta name="twitter:description" content={seo.description} />
        <meta name="twitter:image" content={seo.ogImage} />
        <meta name="twitter:url" content={seo.url} />
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      </Helmet>
      <Navbar />
      <main className="relative z-10">
        <div className="bg-transparent">
          <Hero />
        </div>
        
        <section id="services" className="relative z-10 py-1 md:py-3">
          <Services />
        </section>
        
        <section id="how-we-work" className="relative z-10 py-1 md:py-3">
          <HowWeWork />
        </section>
        
        <div id="who-we-help" className="relative z-10 py-1 md:py-3">
          <TechServices />
        </div>

        <div id="legal-services" className="relative z-10 py-1 md:py-3">
          <LegalServices />
        </div>
        
        {/* Moved KeyMetrics section to appear before Contact section */}
        <div id="metrics" className="relative z-10 py-1 md:py-3">
          <KeyMetrics />
        </div>
        
        <section id="contact" className="relative z-10 py-1 md:py-3">
          <Contact />
        </section>
      </main>
      <Footer />
      <Toaster />
    </div>
  );
};

export default Index;
